<template>
  <LoaderWrapper ref="loader" class="h-100">
    <div class="row m-4 mt-0">
      <div class="col-sm-12">
        <div v-if="!centerEnabled" class="card card-body text-muted text-center py-4">
          {{ helper.trans('notification_center.access_denied') }}
        </div>

        <template v-else-if="broadcastEnabled">
          <div class="card card-body mb-0">
            <ul class="navbar-nav overflow-auto flex-row gap-4">
              <li v-for="tab in tabs" :key="tab" class="nav-item nav-item-spaced d-lg-block text-nowrap">
                <a
                  href="#"
                  @click.prevent="activeTab = tab"
                  class="nav-link tab"
                  :class="{'active': activeTab === tab}"
                >{{ helper.trans(`notification_center.tab.${tab}`) }}</a>
              </li>
            </ul>
          </div>

          <div v-if="activeTab === Tabs.broadcast" class="row m-4 mt-0">
            <div class="col-sm-12">
              <div class="card card-body">
                <div class="row">
                  <div class="col-lg-8">
                    <div class="mb-3">
                      <label class="form-label text-capitalize-first">{{ helper.trans('notification_center.message_text') }}</label>
                      <textarea
                        v-model="broadcastText"
                        class="form-control"
                        rows="6"
                      />
                    </div>

                    <div class="mb-3">
                      <div class="form-check form-check-inline">
                        <input
                          id="audienceModeSaved"
                          class="form-check-input"
                          type="radio"
                          value="saved"
                          v-model="audienceMode"
                        />
                        <label class="form-check-label" for="audienceModeSaved">
                          {{ helper.trans('notification_center.audience_mode.saved') }}
                        </label>
                      </div>
                      <div class="form-check form-check-inline">
                        <input
                          id="audienceModeAdhoc"
                          class="form-check-input"
                          type="radio"
                          value="adhoc"
                          v-model="audienceMode"
                        />
                        <label class="form-check-label" for="audienceModeAdhoc">
                          {{ helper.trans('notification_center.audience_mode.adhoc') }}
                        </label>
                      </div>
                    </div>

                    <div v-if="audienceMode === 'saved'" class="mb-3">
                      <label class="form-label text-capitalize-first">{{ helper.trans('notification_center.audience_name') }}</label>
                      <select
                        v-model="selectedAudienceId"
                        class="form-select form-select-sm"
                      >
                        <option :value="null">{{ helper.trans('labels.select') }}</option>
                        <option v-for="item in audiences" :key="item.id" :value="item.id">{{ item.name }}</option>
                      </select>
                    </div>

                    <div v-else class="mb-3">
                      <TelegramBroadcastAudienceSpecForm v-model="adhocSpec" />
                    </div>

                    <div class="d-flex flex-wrap align-items-center gap-3">
                      <span v-if="previewLoading" class="text-muted">
                        <i class="fa fa-spinner fa-spin"></i>
                      </span>
                      <span v-else-if="previewChatCount !== null" class="text-muted">
                        {{ helper.trans('notification_center.preview_result', {count: previewChatCount}) }}
                      </span>
                      <button
                        type="button"
                        class="btn btn-primary btn-sm text-capitalize-first ms-auto"
                        :disabled="!canSend"
                        @click="sendConfirmModal = true"
                      >{{ helper.trans('notification_center.send') }}</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="activeTab === Tabs.audiences" class="row m-4 mt-0">
            <div class="col-sm-12">
              <div class="card card-body">
                <div class="text-end mb-2">
                  <button type="button" class="btn btn-outline-primary btn-sm text-capitalize" @click="openAudienceModal()">
                    {{ helper.trans('button.create') }}
                  </button>
                </div>
                <div class="overflow-auto">
                  <table class="table table-striped border-light align-middle text-nowrap">
                    <thead>
                    <tr>
                      <th>{{ helper.trans('labels.id') }}</th>
                      <th>{{ helper.trans('labels.name') }}</th>
                      <th>{{ helper.trans('labels.created_at') }}</th>
                      <th>{{ helper.trans('notification_center.author') }}</th>
                      <th class="text-end"></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="item in audiences" :key="item.id">
                      <td>{{ item.id }}</td>
                      <td>{{ item.name }}</td>
                      <td>{{ dateHelper.format(item.createdAt) }}</td>
                      <td>{{ item.createdByName }}</td>
                      <td class="text-end">
                        <button type="button" class="btn btn-sm btn-outline-dark me-2" @click="openAudienceModal(item)">
                          <i class="fa fa-pencil"></i>
                        </button>
                        <button type="button" class="btn btn-sm btn-outline-danger" @click="confirmDeleteAudience(item)">
                          <i class="fa fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div v-if="activeTab === Tabs.journal" class="row m-4 mt-0">
            <div class="col-sm-12">
              <div class="card card-body">
                <div class="overflow-auto">
                  <table class="table table-striped border-light align-middle">
                    <thead>
                    <tr>
                      <th>{{ helper.trans('labels.id') }}</th>
                      <th>{{ helper.trans('labels.created_at') }}</th>
                      <th>{{ helper.trans('notification_center.author') }}</th>
                      <th>{{ helper.trans('notification_center.audience_name') }}</th>
                      <th>{{ helper.trans('notification_center.chat_count') }}</th>
                      <th>{{ helper.trans('notification_center.message_text') }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="item in journal.items" :key="item.id">
                      <td>{{ item.id }}</td>
                      <td class="text-nowrap">{{ dateHelper.format(item.createdAt) }}</td>
                      <td>{{ item.createdByName }}</td>
                      <td>{{ item.audienceName ?? helper.trans('notification_center.one_off_audience') }}</td>
                      <td>{{ item.chatCount }}</td>
                      <td class="text-break" style="max-width: 24rem; white-space: pre-wrap;">{{ item.text }}</td>
                    </tr>
                    </tbody>
                  </table>
                  <b-pagination
                    v-if="journal.totalCount > journal.items.length"
                    v-model="journalPage"
                    :total-rows="journal.totalCount"
                    :per-page="JOURNAL_PAGE_SIZE"
                    :limit="20"
                    @update:modelValue="loadJournal()"
                    prev-text="Back"
                    next-text="Next"
                    align="start"
                    first-number
                    hide-goto-end-buttons
                    hide-ellipsis
                  />
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <b-modal
      :title="audienceModalTitle"
      :modelValue="audienceModalOpen"
      body-class="pt-0"
      @update:modelValue="audienceModalOpen = $event"
    >
      <div class="mb-3">
        <label class="form-label text-capitalize-first">{{ helper.trans('notification_center.audience_name') }}</label>
        <input v-model="audienceFormName" type="text" class="form-control form-control-sm" />
      </div>
      <TelegramBroadcastAudienceSpecForm
        v-if="audienceModalOpen"
        :key="audienceFormKey"
        v-model="audienceFormSpec"
      />
      <template #footer>
        <button type="button" class="btn btn-outline-secondary btn-sm text-capitalize-first" @click="audienceModalOpen = false">
          {{ helper.trans('button.cancel') }}
        </button>
        <LoaderWrapper ref="audienceSaveLoader" :zoom="0.5">
          <button type="button" class="btn btn-outline-primary btn-sm text-capitalize-first" @click="saveAudience">
            {{ helper.trans('button.save') }}
          </button>
        </LoaderWrapper>
      </template>
    </b-modal>

    <b-modal
      :title="helper.trans('labels.delete_confirm')"
      :modelValue="deleteAudienceModalOpen"
      body-class="pt-0"
      @update:modelValue="deleteAudienceModalOpen = $event"
    >
      <p v-if="audienceToDelete">{{ audienceToDelete.name }}</p>
      <template #footer>
        <button type="button" class="btn btn-outline-secondary btn-sm text-capitalize-first" @click="deleteAudienceModalOpen = false">
          {{ helper.trans('button.cancel') }}
        </button>
        <LoaderWrapper ref="deleteAudienceLoader" :zoom="0.5">
          <button type="button" class="btn btn-outline-danger btn-sm text-capitalize-first" @click="deleteAudience">
            {{ helper.trans('button.delete') }}
          </button>
        </LoaderWrapper>
      </template>
    </b-modal>

    <b-modal
      :title="helper.trans('button.confirm')"
      :modelValue="sendConfirmModal"
      body-class="pt-0"
      @update:modelValue="sendConfirmModal = $event"
    >
      <p>{{ helper.trans('notification_center.send_confirm', {count: previewChatCount ?? 0}) }}</p>
      <template #footer>
        <button type="button" class="btn btn-outline-secondary btn-sm text-capitalize-first" @click="sendConfirmModal = false">
          {{ helper.trans('button.cancel') }}
        </button>
        <LoaderWrapper ref="sendLoader" :zoom="0.5">
          <button type="button" class="btn btn-outline-primary btn-sm text-capitalize-first" @click="sendBroadcast">
            {{ helper.trans('button.confirm') }}
          </button>
        </LoaderWrapper>
      </template>
    </b-modal>
  </LoaderWrapper>
</template>

<script lang="ts">
import {computed, defineComponent, reactive, ref, toRefs, watch} from 'vue'
import {useStore} from 'vuex';
import {debounce, helper} from '@/service/CommonHelper';
import {dateHelper} from '@/service/DateHelper';
import LoaderWrapper from '@/components/LoaderWrapper.vue';
import TelegramBroadcastAudienceSpecForm from '@/components/TelegramBroadcastAudienceSpecForm.vue';
import {api} from '@/service/APIClient';
import TelegramBroadcastAudienceDTO from '@/models/TelegramBroadcastAudienceDTO';
import TelegramBroadcastAudienceSpec, {
  createEmptyTelegramBroadcastAudienceSpec,
  isMeaningfulTelegramBroadcastSpec,
  normalizeTelegramBroadcastSpec,
} from '@/models/TelegramBroadcastAudienceSpec';
import PaginatedResponseDTO from '@/models/PaginatedResponseDTO';
import TelegramBroadcastJournalItemDTO from '@/models/TelegramBroadcastJournalItemDTO';
import {useNotification} from '@kyvg/vue3-notification';
import {BPagination} from 'bootstrap-vue-3';
import axios from 'axios';

const notification = useNotification();
const JOURNAL_PAGE_SIZE = 20;

enum Tabs {
  broadcast = 'broadcast',
  audiences = 'audiences',
  journal = 'journal',
}

type AudienceMode = 'saved' | 'adhoc';

export default defineComponent({
  name: 'NotificationCenter',
  components: {
    LoaderWrapper,
    TelegramBroadcastAudienceSpecForm,
    BPagination,
  },
  setup() {
    const store = useStore();
    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null);
    const previewLoading = ref(false);
    let previewRequestId = 0;
    const audienceSaveLoader = ref<InstanceType<typeof LoaderWrapper> | null>(null);
    const deleteAudienceLoader = ref<InstanceType<typeof LoaderWrapper> | null>(null);
    const sendLoader = ref<InstanceType<typeof LoaderWrapper> | null>(null);

    const centerEnabled = computed(() => !!store.getters.getAppData?.TELEGRAM_NOTIFICATION_CENTER_ENABLED);
    const broadcastEnabled = computed(() => !!store.getters.getAppData?.TELEGRAM_BROADCAST_CENTER_ENABLED);

    const state = reactive({
      activeTab: Tabs.broadcast as Tabs,
      audienceMode: 'saved' as AudienceMode,
      selectedAudienceId: null as number | null,
      adhocSpec: createEmptyTelegramBroadcastAudienceSpec(),
      broadcastText: '',
      previewChatCount: null as number | null,
      previewStamp: '',
      audiences: [] as TelegramBroadcastAudienceDTO[],
      audienceModalOpen: false,
      audienceModalTitle: '',
      audienceFormName: '',
      audienceFormSpec: createEmptyTelegramBroadcastAudienceSpec(),
      editingAudienceId: null as number | null,
      audienceFormKey: 0,
      deleteAudienceModalOpen: false,
      audienceToDelete: null as TelegramBroadcastAudienceDTO | null,
      sendConfirmModal: false,
      journalPage: 1,
      journal: {
        limit: JOURNAL_PAGE_SIZE,
        offset: 0,
        totalCount: 0,
        items: [],
      } as PaginatedResponseDTO<TelegramBroadcastJournalItemDTO>,
    });

    const tabs = [Tabs.broadcast, Tabs.audiences, Tabs.journal];

    const audiencePreviewStamp = computed(() => JSON.stringify({
      mode: state.audienceMode,
      audienceId: state.selectedAudienceId,
      spec: normalizeTelegramBroadcastSpec(state.adhocSpec),
    }));

    const canSend = computed(() =>
      state.previewChatCount !== null
      && state.previewStamp === audiencePreviewStamp.value
      && state.broadcastText.trim() !== ''
    );

    const clearPreview = () => {
      state.previewChatCount = null;
      state.previewStamp = '';
    };

    const buildPreviewPayload = (): { audienceId?: number; spec?: TelegramBroadcastAudienceSpec } | null => {
      if (state.audienceMode === 'saved') {
        const audienceId = Number(state.selectedAudienceId);
        if (!audienceId) {
          return null;
        }
        return {audienceId};
      }
      const spec = normalizeTelegramBroadcastSpec(state.adhocSpec);
      if (!isMeaningfulTelegramBroadcastSpec(spec)) {
        return null;
      }
      return {spec};
    };

    const notifyError = (error: unknown, fallback: string) => {
      let message = fallback;
      if (axios.isAxiosError(error)) {
        const data = error.response?.data as { message?: string } | undefined;
        if (data?.message) {
          message = String(data.message);
        }
      }
      notification.notify({title: message, type: 'error'});
    };

    const loadAudiences = async () => {
      state.audiences = await api.telegramBroadcastAudiences();
    };

    const loadJournal = async () => {
      const limit = JOURNAL_PAGE_SIZE;
      const offset = limit * (state.journalPage - 1);
      const response = await api.telegramBroadcasts({limit, offset});
      state.journal = {
        ...response,
        limit,
        offset,
      };
    };

    const loadBroadcastData = async () => {
      if (!broadcastEnabled.value) {
        return;
      }
      loader.value?.show();
      try {
        await Promise.all([loadAudiences(), loadJournal()]);
      } catch (error) {
        notifyError(error, helper.trans('labels.error'));
      } finally {
        loader.value?.hide();
      }
    };

    const refreshPreview = async () => {
      const requestId = ++previewRequestId;
      const payload = buildPreviewPayload();
      if (!payload) {
        clearPreview();
        previewLoading.value = false;
        return;
      }
      previewLoading.value = true;
      try {
        const resp = await api.telegramBroadcastPreview(payload);
        if (requestId !== previewRequestId) {
          return;
        }
        state.previewChatCount = resp.chatCount;
        state.previewStamp = audiencePreviewStamp.value;
      } catch (error) {
        if (requestId !== previewRequestId) {
          return;
        }
        notifyError(error, helper.trans('labels.error'));
        clearPreview();
      } finally {
        if (requestId === previewRequestId) {
          previewLoading.value = false;
        }
      }
    };

    const schedulePreviewRefresh = debounce(() => {
      refreshPreview();
    }, 300);

    const sendBroadcast = async () => {
      const payload = buildPreviewPayload();
      if (!payload || state.previewChatCount === null) {
        notification.notify({
          title: helper.trans('notification_center.preview_required'),
          type: 'warn',
        });
        return;
      }
      sendLoader.value?.show();
      try {
        const resp = await api.telegramBroadcastSend({
          ...payload,
          text: state.broadcastText.trim(),
        });
        notification.notify({
          title: helper.trans('notification_center.send_success', {count: resp.chatCount}),
          type: 'success',
        });
        state.sendConfirmModal = false;
        state.broadcastText = '';
        state.activeTab = Tabs.journal;
      } catch (error) {
        notifyError(error, helper.trans('labels.error'));
      } finally {
        sendLoader.value?.hide();
      }
    };

    const openAudienceModal = (item?: TelegramBroadcastAudienceDTO) => {
      state.audienceFormKey += 1;
      if (item) {
        state.editingAudienceId = item.id;
        state.audienceModalTitle = helper.trans('button.edit');
        state.audienceFormName = item.name;
        state.audienceFormSpec = normalizeTelegramBroadcastSpec(item.spec);
      } else {
        state.editingAudienceId = null;
        state.audienceModalTitle = helper.trans('button.create');
        state.audienceFormName = '';
        state.audienceFormSpec = createEmptyTelegramBroadcastAudienceSpec();
      }
      state.audienceModalOpen = true;
    };

    const saveAudience = async () => {
      if (!state.audienceFormName.trim()) {
        notification.notify({
          title: helper.trans('notification_center.validation.name_required'),
          type: 'warn',
        });
        return;
      }
      const spec = normalizeTelegramBroadcastSpec(state.audienceFormSpec);
      if (!isMeaningfulTelegramBroadcastSpec(spec)) {
        notification.notify({
          title: helper.trans('notification_center.validation.audience_required'),
          type: 'warn',
        });
        return;
      }
      audienceSaveLoader.value?.show();
      try {
        if (state.editingAudienceId !== null) {
          await api.telegramBroadcastAudienceUpdate(state.editingAudienceId, state.audienceFormName.trim(), spec);
        } else {
          await api.telegramBroadcastAudienceCreate(state.audienceFormName.trim(), spec);
        }
        state.audienceModalOpen = false;
        await loadAudiences();
      } catch (error) {
        notifyError(error, helper.trans('labels.error'));
      } finally {
        audienceSaveLoader.value?.hide();
      }
    };

    const confirmDeleteAudience = (item: TelegramBroadcastAudienceDTO) => {
      state.audienceToDelete = item;
      state.deleteAudienceModalOpen = true;
    };

    const deleteAudience = async () => {
      if (!state.audienceToDelete) {
        return;
      }
      deleteAudienceLoader.value?.show();
      try {
        await api.telegramBroadcastAudienceDelete(state.audienceToDelete.id);
        if (Number(state.selectedAudienceId) === state.audienceToDelete.id) {
          state.selectedAudienceId = null;
        }
        state.deleteAudienceModalOpen = false;
        state.audienceToDelete = null;
        await loadAudiences();
      } catch (error) {
        notifyError(error, helper.trans('labels.error'));
      } finally {
        deleteAudienceLoader.value?.hide();
      }
    };

    watch(audiencePreviewStamp, () => {
      schedulePreviewRefresh();
    });

    watch(() => state.activeTab, (tab) => {
      if (tab === Tabs.journal && broadcastEnabled.value) {
        loadJournal();
      }
    });

    watch(broadcastEnabled, (enabled) => {
      if (enabled) {
        loadBroadcastData();
      }
    }, {immediate: true});

    return {
      ...toRefs(state),
      loader,
      previewLoading,
      audienceSaveLoader,
      deleteAudienceLoader,
      sendLoader,
      centerEnabled,
      broadcastEnabled,
      tabs,
      Tabs,
      helper,
      dateHelper,
      canSend,
      sendBroadcast,
      openAudienceModal,
      saveAudience,
      confirmDeleteAudience,
      deleteAudience,
      loadJournal,
      JOURNAL_PAGE_SIZE,
    };
  },
});
</script>
